import React, { useState, useRef, useEffect } from "react";
import { useBar } from "../../context/BarContext";

/**
 * @param {object} props
 * @param {boolean} [props.showWhenNoBars=false] - When true, render a disabled control with emptyLabel if there are no bars (e.g. bar admins waiting for invites).
 * @param {string} [props.emptyBarLabel="No bars assigned"] - Label when showWhenNoBars and userBars is empty.
 */
const BarSelector = ({
  showWhenNoBars = false,
  emptyBarLabel = "No bars assigned",
}) => {
  const { selectedBar, userBars, setSelectedBar } = useBar();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelectBar = (bar) => {
    setSelectedBar(bar);
    setIsOpen(false);
  };

  if (!userBars || userBars.length === 0) {
    if (!showWhenNoBars) {
      return null;
    }
    return (
      <div className="bar-selector-container">
        <button
          type="button"
          className="bar-selector-button bar-selector-button--empty"
          disabled
          aria-disabled="true"
          aria-label={emptyBarLabel}
        >
          <span className="bar-selector-text">{emptyBarLabel}</span>
        </button>
      </div>
    );
  }

  return (
    <div className="bar-selector-container" ref={dropdownRef}>
      <button
        className="bar-selector-button"
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <span className="bar-selector-text">
          {selectedBar?.name || "Select a bar"}
        </span>
        <span className={`bar-selector-arrow ${isOpen ? "open" : ""}`}>▼</span>
      </button>

      {isOpen && (
        <div className="bar-selector-dropdown">
          {userBars.map((bar) => (
            <button
              key={bar.bar_id}
              className={`bar-selector-option ${
                selectedBar?.bar_id === bar.bar_id ? "selected" : ""
              }`}
              onClick={() => handleSelectBar(bar)}
              type="button"
            >
              <span className="bar-option-name">{bar.name}</span>
              {selectedBar?.bar_id === bar.bar_id && (
                <span className="bar-option-check">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default BarSelector;
